// HeroForge's slots (docs/80): each choice is one row that names what is chosen now; its tiles
// open only when the row is clicked, one slot at a time. The choice itself lives in HeroForge.
import type { ReactNode } from 'react';

export type SlotKey = 'class' | 'calling' | 'species' | 'background' | 'loadout';

export interface SlotProps {
  slot: SlotKey;
  label: string;
  open: boolean;
  onToggle: (slot: SlotKey) => void;
  /** The current pick's art (class icon, beast sprite, item icon). */
  art?: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  /** Something must still be chosen here (the Forge button is held): the row is drawn amber. */
  wants?: boolean;
  /** Beside the head, outside its button (a details button, a count badge). */
  aside?: ReactNode;
  /** Under the head whether or not the slot is open (a companion's name). */
  after?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}

export function Slot({ slot, label, open, onToggle, art, value, sub, wants, aside, after, hint, children }: SlotProps) {
  const body = `fk-forge-slot-${slot}`;
  return (
    <section className={`fk-forge__slot ${open ? 'fk-forge__slot--open' : ''} ${wants ? 'fk-forge__slot--wants' : ''}`} aria-label={label} data-slot={slot}>
      <div className="fk-forge__slot-row">
        <button type="button" className="fk-forge__slot-head" aria-expanded={open} aria-controls={body} data-testid="forge-slot" data-slot={slot}
          data-sfx="ui.tab" onClick={() => onToggle(slot)}>
          <span className="fk-forge__slot-label">{label}</span>
          <span className="fk-forge__slot-art" aria-hidden={!art}>{art}</span>
          <span className="fk-forge__slot-value">
            <span className="fk-forge__slot-name" data-testid="forge-slot-value">{value}</span>
            {sub && <span className="fk-forge__slot-sub">{sub}</span>}
          </span>
          <span className="fk-forge__slot-cta">{open ? 'Close' : wants ? 'Choose' : 'Change'}<span className="fk-forge__slot-chev" aria-hidden>▾</span></span>
        </button>
        {aside && <span className="fk-forge__slot-aside">{aside}</span>}
      </div>
      {after}
      {open && (
        <div className="fk-forge__slot-body" id={body}>
          {hint && <p className="fk-forge__hint fk-forge__slot-hint">{hint}</p>}
          {children}
        </div>
      )}
    </section>
  );
}
